<template>
  <DropDownSelect :disabled="disabled">
    <template #activator="{ open, showDropDown }">
      <button
        class="flex items-center gap-1 rounded-sm border border-solid p-2 text-body-s"
        :class="[
          modelValue?.length || showDropDown ? 'filter-selected' : 'filter-default',
          disabled ? '!border-disabled !text-disabled' : '',
        ]"
        @click.prevent="open"
      >
        {{ label }}
        <span v-if="props.modelValue?.length" class="rounded-xl bg-surface-elevation-4 p-1 text-button-m">
          {{ props.modelValue.length }}
        </span>
        <ChevronDownIcon class="lucide-icon-m" />
      </button>
    </template>

    <template #default="{ close }">
      <ul class="options-list m-0 list-none overflow-y-auto p-0">
        <slot name="header" :close="close" />
        <div v-if="props.options.length === 0" class="p-2">{{ noOptionsLabel }}</div>
        <li v-for="option in props.options" :key="`filter_${option.value}`" class="p-2 hover:bg-surface-elevation-2">
          <CheckBox
            :model-value="props.modelValue?.includes(option.value)"
            class="whitespace-nowrap text-main"
            @update:model-value="handleCheckboxChange(option.value, $event)"
          >
            <div v-if="option.renderType === 'ratingStars'" class="flex items-center">
              <span v-if="option.renderType === 'ratingStars'" class="pr-4">
                <RatingStars :value="parseInt(option.text)" />
              </span>
              {{ option.text }}
            </div>
            <div v-else-if="option.renderType === 'stateBadges'">
              <ChipBadge :color="option.color ?? colorByState(option.responseState)" rounded>
                {{ option.text }}
              </ChipBadge>
            </div>
            <div v-else>
              {{ option.text }}
            </div>
          </CheckBox>
        </li>
      </ul>
    </template>
  </DropDownSelect>
</template>

<script setup lang="ts">
import { RatingStars, ChipBadge } from '@ramp106/omrjs-core-ui'
import { ChevronDownIcon } from '@ramp106/omrjs-core-ui/icons'

import { SurveyResponseState } from '@/composables/useSurveyResponseState'

import { type reviewsFilterMultiSelectOption } from './types'

const props = defineProps<{
  modelValue?: string[]
  options: reviewsFilterMultiSelectOption[]
  label: string
  disabled?: boolean
  noOptionsLabel?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
}>()

const handleCheckboxChange = (optionValue: string, checked: boolean) => {
  if (checked) {
    const newOptions = props.modelValue?.slice() || []
    newOptions.push(optionValue)

    emit('update:modelValue', newOptions)
  } else {
    const newOptions = (props.modelValue?.slice() || []).filter((option) => option !== optionValue)
    emit('update:modelValue', newOptions)
  }
}

function colorByState(responseState: SurveyResponseState | undefined) {
  switch (responseState) {
    case SurveyResponseState.Published:
      return 'mint'
    case SurveyResponseState.Drafted:
    case SurveyResponseState.ReDrafted:
      return 'purple'
    case SurveyResponseState.Rejected:
      return 'error'
    case SurveyResponseState.Proposed:
      return 'yellow'
    case SurveyResponseState.Stale:
      return 'default'
    default:
      return 'default'
  }
}
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

.options-list {
  max-height: 640px;
}
.filter-selected {
  @apply border-strong text-subtle;
}
.filter-default {
  @apply border-main text-subtlest disabled:border-disabled disabled:text-disabled;
}
</style>
